import {
    DiscountAppliesTo,
    DiscountGetDiscountType,
    DiscountGetItemFrom,
    DiscountMinimumRequirement,
    DiscountType,
} from 'models'
import * as yup from 'yup'
import { IUpsertDiscountForm } from '../utils/IUpsertDiscountForm'
import nameof from 'utilities/nameof'
import { isNil } from 'lodash-es'
export const SIMPLE_DISCOUNT_TYPE = [
    DiscountType.FixedAmount,
    DiscountType.Percentage,
]

export const FUTURE_DISCOUNT_TYPE = [DiscountType.FutureDiscount]

export const FORM_FIELDS = nameof<IUpsertDiscountForm>()
const availabilitySchema = yup.object().shape({
    timeStart: yup
        .mixed()
        .nullable()
        .when(['allDay', 'isExpiration'], {
            is: (allDay: boolean, isExpiration: boolean) =>
                isExpiration && !allDay,
            then: (s) => s.required('Start time is required'),
        }),
    timeEnd: yup
        .mixed()
        .nullable()
        .when(['allDay', 'isExpiration'], {
            is: (allDay: boolean, isExpiration: boolean) =>
                isExpiration && !allDay,
            then: (s) => s.required('End time is required'),
        }),
    duration: yup
        .mixed()
        .nullable()
        .test('duration-required', 'Duration is required', (val, context) => {
            if (
                (val as any)?.some((v: any) => isNil(v)) &&
                context.parent.isExpiration
            )
                return false
            return true
        }),
})

const useUpsertDiscountSchema = () => {
    const validateSchema = yup
        .object()
        .shape({
            [FORM_FIELDS.name]: yup.string().required(),
            [FORM_FIELDS.value]: yup
                .number()
                .nullable()
                .when(FORM_FIELDS.type, {
                    is: (type: DiscountType) => {
                        return type === DiscountType.FixedAmount
                    },
                    then: (s) => s.min(0).required(),
                })
                .when(FORM_FIELDS.type, {
                    is: (type: DiscountType) => {
                        return type === DiscountType.Percentage
                    },
                    then: (s) =>
                        s
                            .min(0, 'Value should be 0-100')
                            .max(100, 'Value should be 0-100')
                            .required(),
                }),
            [FORM_FIELDS.applyToIDs]: yup
                .array()
                .when([FORM_FIELDS.type, FORM_FIELDS.appliesTo], {
                    is: (type: DiscountType, appliesTo: DiscountAppliesTo) => {
                        return (
                            SIMPLE_DISCOUNT_TYPE.includes(type) &&
                            (appliesTo ===
                                DiscountAppliesTo.SelectedCategories ||
                                appliesTo ===
                                    DiscountAppliesTo.SelectedProducts)
                        )
                    },
                    then: (s) => s.min(1).required(),
                }),
            [FORM_FIELDS.minRequirementValue]: yup
                .number()
                .nullable()
                .when([FORM_FIELDS.minRequirement], {
                    is: (minReq: DiscountMinimumRequirement) =>
                        minReq !== DiscountMinimumRequirement.None,
                    then: (s) =>
                        s
                            .min(
                                1,
                                'The minimum requirement must be greater than 0.',
                            )
                            .required(),
                }),
            [FORM_FIELDS.buyIDs]: yup.array().when(FORM_FIELDS.type, {
                is: DiscountType.BuyXGetY,
                then: (s) => s.min(1, 'Please select at least one').required(),
            }),
            [FORM_FIELDS.getQty]: yup
                .number()
                .nullable()
                .when([FORM_FIELDS.type], {
                    is: (type: DiscountType) => type === DiscountType.BuyXGetY,
                    then: (s) =>
                        s
                            .min(1, 'The quantity must be greater than 0.')
                            .required('Quantity is required'),
                }),
            [FORM_FIELDS.getDiscountValue]: yup
                .number()
                .nullable()
                .when([FORM_FIELDS.getAtDiscountType], {
                    is: (type: DiscountGetDiscountType) =>
                        type === DiscountGetDiscountType.Percentage,
                    then: (s) =>
                        s
                            .required('Discount value is required')
                            .min(0)
                            .max(100),
                })
                .when([FORM_FIELDS.getAtDiscountType], {
                    is: (type: DiscountGetDiscountType) =>
                        type === DiscountGetDiscountType.FixedAmount,
                    then: (s) =>
                        s.required('Discount value is required').min(0),
                }),
            [FORM_FIELDS.getIDs]: yup
                .array()
                .when([FORM_FIELDS.type, FORM_FIELDS.getAnyItemFrom], {
                    is: (
                        type: DiscountType,
                        getAnyItemFrom: DiscountGetItemFrom,
                    ) =>
                        type === DiscountType.BuyXGetY &&
                        getAnyItemFrom !==
                            DiscountGetItemFrom.SameAsPurchasedProduct,
                    then: (s) =>
                        s.min(1, 'Please select at least one').required(),
                }),
            [FORM_FIELDS.nextDiscountValue]: yup
                .number()
                .nullable()
                .when([FORM_FIELDS.nextDiscountType, FORM_FIELDS.type], {
                    is: (nextType: DiscountType, type: DiscountType) =>
                        nextType !== DiscountType.FutureDiscount &&
                        type === DiscountType.FutureDiscount,
                    then: (s) => s.required().min(0),
                }),
            [FORM_FIELDS.futureOrderDiscountID]: yup
                .string()
                .when([FORM_FIELDS.type, FORM_FIELDS.nextDiscountType], {
                    is: (type: DiscountType, nextType: DiscountType) => {
                        return (
                            type === DiscountType.FutureDiscount &&
                            nextType === DiscountType.FutureDiscount
                        )
                    },
                    then: (s) => s.required('Please select next discount'),
                }),
            [FORM_FIELDS.code]: yup.string().notRequired(),
            [FORM_FIELDS.orderChannelIDs]: yup
                .array()
                .required()
                .min(1, 'Please select at least one'),
            [FORM_FIELDS.fulfillmentIDs]: yup
                .array()
                .required()
                .min(1, 'Please select at least one'),
            [FORM_FIELDS.discountAvailabilities]: yup
                .array()
                .of(availabilitySchema),
            [FORM_FIELDS.discountMenu]: yup.object().shape({
                applyToOnlineMenu: yup.boolean(),
                menuDiscounts: yup
                    .array()
                    .of(
                        yup.object().shape({
                            menuID: yup.string().required('Menu is Required'),
                            itemIDs: yup
                                .array()
                                .of(yup.string())
                                .test(
                                    'item-required',
                                    'Products is required',
                                    (val, context) => {
                                        if (
                                            (context as any).from[2].value
                                                .isItemDiscount
                                        )
                                            return (val?.length ?? 0) > 0
                                        return true
                                    },
                                ),
                        }),
                    )
                    .when(['applyToOnlineMenu'], {
                        is: (applyToOnlineMenu: boolean) => applyToOnlineMenu,
                        then: (s) => s.min(1, 'Please select at least one'),
                    })
                    .test(
                        'menu-duplicate',
                        'Online Menu are Duplidated',
                        (menus) => {
                            if ((menus?.length ?? 0) === 0) return true
                            const validOptions =
                                menus?.map((a) => a.menuID)?.filter((a) => a) ??
                                []
                            return (
                                validOptions.length ===
                                new Set(validOptions)?.size
                            )
                        },
                    ),
            }),
        })
        .defined()
    return validateSchema
}
export default useUpsertDiscountSchema
